import { ReadonlyURLSearchParams } from 'next/navigation';
import { pathToRegexp } from 'path-to-regexp';

import { BoundaryProjectAssignmentStatus } from '@/apis/report/boundary-projects/users/assignment-status.dto';
import { EmissionSourceCategory, EmissionSourceSubCategory } from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import { DataEntryTabType } from '@/app/product-cfv/data-entry/_components/DataEntry';
import { ProjectFlowType } from '@/components/common/Layout/ProjectFlow/types';
import { UUID_REGEX_STRING } from '@/constants/regex';
import { IO_ROUTES, IP_ROUTES, ROUTE_CONFIG } from '@/routes/constants';
import { Route } from '@/routes/enum';
import { ScenarioConfigurationType } from '@/typings/common';
import { Role } from '@/typings/user';

const extractUUIDInPath = (pathname: string) => pathname.match(new RegExp(UUID_REGEX_STRING, 'g'));

export const getUrlByRouteAndPathname = ({
  route,
  pathname,
  searchParams,
}: {
  route?: Route;
  pathname: string;
  searchParams: ReadonlyURLSearchParams;
}) => {
  if (!route) {
    return undefined;
  }
  const ids = extractUUIDInPath(pathname) || [];

  switch (route) {
    // Account Management
    case Route.ACCOUNT_MANAGEMENT_CREATE_ACCOUNT:
      return ROUTE_CONFIG[route].getFullUrl({ departmentId: searchParams.get('departmentId') || '' });
    case Route.ACCOUNT_MANAGEMENT_EDIT_ACCOUNT:
      return ROUTE_CONFIG[route].getFullUrl({ accountId: ids[0] || '' });
    case Route.ACCOUNT_MANAGEMENT_VIEW_ACCOUNT:
      return ROUTE_CONFIG[route].getFullUrl({ accountId: ids[0] || '' });
    case Route.ACCOUNT_MANAGEMENT_EDIT_DEPARTMENT:
      return ROUTE_CONFIG[route].getFullUrl({ departmentId: ids[0] || '' });

    // Project CFV
    case Route.ORG_CFV_DASHBOARD:
    case Route.ORG_CFV_PROJECT_EMISSION_IDENTIFICATION:
    case Route.ORG_CFV_PROJECT_EMISSION_IDENTIFICATION_EDIT:
    case Route.ORG_CFV_PROJECT_EMISSION_SOURCES:
    case Route.ORG_CFV_PROJECT_ACTIVITY_DATA_APPROVAL:
    case Route.ORG_CFV_DATA_QUALITY_MGMT:
    case Route.ORG_CFV_DATA_QUALITY_MGMT_DESCRIPTION:
    case Route.ORG_CFV_UNCERTAINTY_QUANTITATIVE_ASSESSMENT:
      return ROUTE_CONFIG[route].getFullUrl({ projectId: ids[0] || '' });
    case Route.ORG_CFV_PROJECT_CREATE_EMISSION_SOURCE:
    case Route.ORG_CFV_PROJECT_RELEVANT_EMISSION_SOURCE:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: ids[0] || '',
        subCategory: (searchParams.get('subCategory') || '') as EmissionSourceSubCategory,
      });
    case Route.ORG_CFV_PROJECT_EDIT_EMISSION_SOURCE:
      return ROUTE_CONFIG[route].getFullUrl({ projectId: ids[0] || '', emissionSourceId: ids[1] || '' });
    case Route.ORG_CFV_PROJECT_VIEW_EMISSION_SOURCE:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: ids[0] || '',
        emissionSourceId: ids[1] || '',
        relatedId: searchParams.get('relatedId') || '',
      });
    case Route.ORG_CFV_PROJECT_ACTIVITY_DATA_ENTRY:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: ids[0] || '',
        category: (searchParams.get('category') || '') as EmissionSourceCategory,
        subCategory: (searchParams.get('subCategory') || '') as EmissionSourceSubCategory,
        emissionId: searchParams.get('emissionId') || '',
      });
    case Route.ORG_CFV_PROJECT_CREATE_ACTIVITY_DATA_ENTRY:
    case Route.ORG_CFV_PROJECT_CREATE_ACTIVITY_DATA_ENTRY_BULK:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: ids[0] || '',
        emissionSourceId: searchParams.get('emissionSourceId') || '',
        subCategory: (searchParams.get('subCategory') || '') as EmissionSourceSubCategory,
      });
    case Route.ORG_CFV_PROJECT_EDIT_ACTIVITY_DATA_ENTRY:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: ids[0] || '',
        activityId: searchParams.get('activityId') || '',
      });
    case Route.ORG_CFV_PROJECT_EDIT_ACTIVITY_DATA_ENTRY_BULK:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: ids[0] || '',
        emissionSourceId: searchParams.get('emissionSourceId') || '',
        approvalId: searchParams.get('approvalId') || '',
        subCategory: (searchParams.get('subCategory') || '') as EmissionSourceSubCategory,
      });
    case Route.ORG_CFV_PROJECT_ACTIVITY_DATA_ENTRY_DETAILS:
    case Route.ORG_CFV_PROJECT_ACTIVITY_DATA_APPROVAL_DETAILS:
    case Route.ORG_CFV_PROJECT_DATA_COLLECTION_DETAILS:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: ids[0] || '',
        activityId: ids[1] || '',
        emissionId: searchParams.get('emissionId') || '',
        approvalId: searchParams.get('approvalId') || '',
        subCategory: (searchParams.get('subCategory') || '') as EmissionSourceSubCategory,
      });
    case Route.ORG_CFV_PROJECT_DATA_COLLECTION:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: ids[0] || '',
        category: (searchParams.get('category') || '') as EmissionSourceCategory,
        subCategory: (searchParams.get('subCategory') || '') as EmissionSourceSubCategory,
        emissionGroupId: searchParams.get('emissionGroupId') || '',
        emissionId: searchParams.get('emissionId') || '',
      });

    // Boundary Database
    case Route.EDIT_VENDOR:
      return ROUTE_CONFIG[route].getFullUrl({ vendorId: searchParams.get('id') || '' });
    case Route.EDIT_RAW_MATERIAL:
    case Route.RAW_MATERIAL_DETAIL:
      return ROUTE_CONFIG[route].getFullUrl({ rawMaterialId: searchParams.get('rawMaterialId') || '' });
    case Route.EDIT_RAW_MATERIAL_BULK:
      return ROUTE_CONFIG[route].getFullUrl({
        selectAll: searchParams.get('selectAll') === 'true',
        selectedIds: searchParams.get('selectedIds')?.split(',') || [],
        partNoFuzzyName: searchParams.get('partNoFuzzyName') || '',
        vendorFuzzyName: searchParams.get('vendorFuzzyName') || '',
        shipmentLocationFuzzyName: searchParams.get('shipmentLocationFuzzyName') || '',
        excludedIds: searchParams.get('excludedIds')?.split(',') || [],
      });
    case Route.CREATE_RAW_MATERIAL_TRANSPORTATION:
      return ROUTE_CONFIG[route].getFullUrl({
        vendorCode: searchParams.get('vendorCode') || '',
        manufacturerCode: searchParams.get('manufacturerCode') || '',
      });
    case Route.EDIT_RAW_MATERIAL_TRANSPORTATION:
    case Route.EDIT_WASTE_TRANSPORTATION:
    case Route.EDIT_DISTRIBUTION_TRANSPORTATION:
      return ROUTE_CONFIG[route].getFullUrl({ transportationId: searchParams.get('transportationId') || '' });

    // Trial pages
    case Route.VIEW_VENDOR_MASTER_TRIAL:
    case Route.EDIT_VENDOR_MASTER_TRIAL:
    case Route.VIEW_RAW_MATERIAL_TRANSPORTATION_TRIAL:
    case Route.EDIT_RAW_MATERIAL_TRANSPORTATION_TRIAL:
      return ROUTE_CONFIG[route].getFullUrl({ id: searchParams.get('id') || '' });
    case Route.EDIT_PRODUCT_LINE:
      return ROUTE_CONFIG[route].getFullUrl({ productLineId: searchParams.get('id') || '' });
    case Route.CREATE_PRODUCT_SERIES:
      return ROUTE_CONFIG[route].getFullUrl({ productLineId: searchParams.get('productLineId') || '' });
    case Route.CREATE_PRODUCT_SERIES_BULK:
      return ROUTE_CONFIG[route].getFullUrl({ productLineId: searchParams.get('productLineId') || '' });
    case Route.EDIT_PRODUCT_SERIES:
    case Route.PRODUCT_MATERIALS:
      return ROUTE_CONFIG[route].getFullUrl({
        productLineId: searchParams.get('productLineId') || '',
        productSeriesId: searchParams.get('productSeriesId') || '',
      });
    case Route.USAGE_SCENARIOS:
      return ROUTE_CONFIG[route].getFullUrl({
        productLineId: searchParams.get('productLineId') || '',
        productSeriesId: searchParams.get('productSeriesId') || '',
        scenarioConfigurationType: (searchParams.get('type') as ScenarioConfigurationType) || undefined,
      });
    case Route.CREATE_USAGE_SCENARIOS:
    case Route.END_OF_LIFE_SCENARIOS_CREATE:
      return ROUTE_CONFIG[route].getFullUrl({
        productLineId: searchParams.get('productLineId') || '',
        productSeriesId: searchParams.get('productSeriesId') || '',
        scenarioCountryAreaId: searchParams.get('scenarioCountryAreaId') || '',
      });
    case Route.EDIT_USAGE_SCENARIOS:
    case Route.USAGE_SCENARIOS_DETAIL:
    case Route.END_OF_LIFE_SCENARIOS_EDIT:
    case Route.END_OF_LIFE_SCENARIOS_DETAIL:
      return ROUTE_CONFIG[route].getFullUrl({
        productLineId: searchParams.get('productLineId') || '',
        productSeriesId: searchParams.get('productSeriesId') || '',
        scenarioCountryAreaId: searchParams.get('scenarioCountryAreaId') || '',
        scenarioId: searchParams.get('scenarioId') || '',
      });
    case Route.EMPLOYEE_COMMUTING:
    case Route.EMPLOYEE_COMMUTING_CREATE:
      return ROUTE_CONFIG[route].getFullUrl();
    case Route.EMPLOYEE_COMMUTING_EDIT:
      return ROUTE_CONFIG[route].getFullUrl({ employeeTransportationId: searchParams.get('id') || '' });

    // Product CFV
    case Route.PRODUCT_CFV_ENERGY_RESOURCES:
    case Route.PRODUCT_CFV_SUPPORTING_DOCUMENT:
    case Route.PRODUCT_CFV_DASHBOARD:
    case Route.PRODUCT_CFV_REPORTING:
    case Route.MULTI_BOUNDARY_REPORT_EXPORT:
    case Route.PRODUCT_CFV_DATA_COLLECTION:
    case Route.PRODUCT_CFV_ENERGY_RESOURCES_RESULT:
    case Route.PRODUCT_CFV_DATA_QUALITY:
    case Route.PRODUCT_CFV_SENSITIVITY_ANALYSIS:
    case Route.PROJECT_SETTINGS_PRODUCT_SERIES:
      return ROUTE_CONFIG[route].getFullUrl({ projectId: searchParams.get('projectId') || '' });
    case Route.PRODUCT_CFV_DATA_ENTRY:
    case Route.PRODUCT_CFV_DATA_APPROVAL:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: searchParams.get('projectId') || '',
        type: (searchParams.get('type') as DataEntryTabType) || undefined,
      });
    case Route.PRODUCT_CFV_DATA_ENTRY_BULK:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: searchParams.get('projectId') || '',
        approvalId: searchParams.get('approvalId') || '',
        type: (searchParams.get('type') as DataEntryTabType) || undefined,
      });
    case Route.PRODUCT_CFV_DATA_COLLECTION_RAW_MATERIAL_APPROVAL:
    case Route.PRODUCT_CFV_DATA_COLLECTION_MANUFACTURING_APPROVAL:
    case Route.PRODUCT_CFV_DATA_COLLECTION_SALES_INFORMATION_APPROVAL:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: searchParams.get('projectId') || '',
        approvalId: searchParams.get('approvalId') || '',
      });
    case Route.PRODUCT_CFV_DATA_ENTRY_DETAIL:
    case Route.PRODUCT_CFV_DATA_APPROVAL_DETAIL:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: searchParams.get('projectId') || '',
        type: searchParams.get('type') as DataEntryTabType,
        approvalId: searchParams.get('approvalId') || '',
      });
    case Route.PRODUCT_CFV_DATA_COLLECTION_DETAIL:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: searchParams.get('projectId') || '',
        refProductSeriesId: searchParams.get('refProductSeriesId') || '',
        productSeriesId: searchParams.get('productSeriesId') || '',
      });
    case Route.PRODUCT_CFV_ENERGY_RESOURCES_RESULT_DETAIL:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: searchParams.get('projectId') || '',
        refProductSeriesId: searchParams.get('refProductSeriesId') || '',
        collectionId: searchParams.get('collectionId') || '',
      });
    case Route.PROJECT_SETTINGS_PRODUCT_SERIES_DETAILS:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: searchParams.get('projectId') || '',
        productSeriesId: searchParams.get('productSeriesId') || '',
        refProductSeriesId: searchParams.get('refProductSeriesId') || '',
      });
    case Route.PRODUCT_CFV_DATA_QUALITY_DETAIL:
    case Route.PRODUCT_CFV_SENSITIVITY_ANALYSIS_DETAIL:
    case Route.PRODUCT_CFV_EDIT_SENSITIVITY_ANALYSIS:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: searchParams.get('projectId') || '',
        productSeriesId: searchParams.get('productSeriesId') || '',
        refProductSeriesId: searchParams.get('refProductSeriesId') || '',
      });
    case Route.PRODUCT_CFV_REPORTING_EDIT:
    case Route.PRODUCT_CFV_REPORTING_PREVIEW:
      return ROUTE_CONFIG[route].getFullUrl({
        projectId: searchParams.get('projectId') || '',
        templateId: searchParams.get('templateId') || '',
      });

    case Route.PROJECT_SETTINGS_EDIT:
      return ROUTE_CONFIG[route].getFullUrl({
        boundaryProjectId: searchParams.get('boundaryProjectId') || '',
        openProductSeriesModal: searchParams.get('openProductSeriesModal') || 'false',
      });

    case Route.OSM_PARENT_ORGANIZATIONAL_UNIT_EDIT:
    case Route.OSM_PARENT_ORGANIZATIONAL_UNIT_VIEW:
    case Route.OSM_ORGANIZATIONAL_UNIT_EDIT:
    case Route.OSM_ORGANIZATIONAL_UNIT_VIEW:
      return ROUTE_CONFIG[route].getFullUrl({
        orgUnitId: searchParams.get('orgUnitId') || '',
      });

    case Route.SUPPLIER_MANAGEMENT_DATA_REQUEST_DETAILS:
      return ROUTE_CONFIG[route].getFullUrl({
        requestId: searchParams.get('requestId') || '',
      });
    case Route.DASHBOARD_PREVIEW_FILE_CREATE:
    case Route.DASHBOARD_CHECK_UPLOAD_INVENTORY_PREVIEW_FILE_CREATE:
      return ROUTE_CONFIG[route].getFullUrl({
        jobId: searchParams.get('jobId'),
        fileId: searchParams.get('fileId') || '',
        fileName: searchParams.get('fileName') || '',
      });
    case Route.DASHBOARD_CHECK_UPLOAD_INVENTOR_PREVIEW_FILE_EDIT:
      return ROUTE_CONFIG[route].getFullUrl({
        spaceId: searchParams.get('spaceId'),
        inventoryId: searchParams.get('spaceId'),
      });

    // Routes without any parameters
    case Route.ROOT:
    case Route.LOGIN:
    case Route.RESET_PASSWORD:
    case Route.DOCUMENT_VIEWER:
    case Route.SELECT_COMPANY:
    case Route.FORBIDDEN:
    case Route.SYSTEM_ERROR:
    case Route.ACCOUNT_MANAGEMENT:
    case Route.ACCOUNT_MANAGEMENT_CREATE_DEPARTMENT:
    case Route.VENDOR:
    case Route.CREATE_VENDOR_BULK:
    case Route.CREATE_VENDOR:
    case Route.RAW_MATERIAL:
    case Route.CREATE_RAW_MATERIAL:
    case Route.CREATE_RAW_MATERIAL_BULK:
    case Route.RAW_MATERIAL_TRANSPORTATION:
    case Route.WASTE_TRANSPORTATION:
    case Route.CREATE_WASTE_TRANSPORTATION:
    case Route.CREATE_WASTE_TRANSPORTATION_BULK:
    case Route.DISTRIBUTION_TRANSPORTATION:
    case Route.CREATE_DISTRIBUTION_TRANSPORTATION:
    case Route.CREATE_DISTRIBUTION_TRANSPORTATION_BULK:
    case Route.PRODUCT_LINE:
    case Route.CREATE_PRODUCT_LINE:
    case Route.CREATE_PRODUCT_LINE_BULK:
    case Route.SCENARIOS_CONFIGURATION:
    case Route.VENDOR_MASTER_TRIAL:
    case Route.CREATE_VENDOR_MASTER_TRIAL:
    case Route.RAW_MATERIAL_TRANSPORTATION_TRIAL:
    case Route.CREATE_RAW_MATERIAL_TRANSPORTATION_TRIAL:
    case Route.CREATE_BULK_RAW_MATERIAL_TRANSPORTATION_TRIAL:

    case Route.MEMBER_PROFILE:
    case Route.MEMBER_MANUAL:
    case Route.MEMBER_CHANGE_PASSWORD:
    case Route.MEMBER_BILLING_INFORMATION:
    case Route.MEMBER_NOTIFICATION_SETTINGS:
    case Route.MEMBER_FAQ_AND_SUPPORT:
    case Route.MEMBER_LEGAL_DOCUMENTS:
    case Route.SINGLE_BOUNDARY_REPORT_EXPORT:
    case Route.ORGANIZATIONAL_STRUCTURE_MANAGEMENT:
    case Route.OSM_PARENT_ORGANIZATIONAL_UNIT_CREATE:
    case Route.OSM_ORGANIZATIONAL_UNIT_CREATE:
    case Route.SUPPLIER_MANAGEMENT_DATA_REQUEST:
    case Route.SUPPLIER_MANAGEMENT_DATA_REQUEST_CREATE:
    case Route.PROJECT_SETTINGS:
    case Route.DASHBOARD:
    case Route.DASHBOARD_DEMO:
    case Route.DASHBOARD_UPLOAD_INVENTORY:
    case Route.DASHBOARD_CHECK_UPLOAD_INVENTORY:
    case Route.DASHBOARD_CHECK_UPLOAD_INVENTORY_UPLOAD_INVENTORY:
    case Route.EMISSION_OVERVIEW:
      return ROUTE_CONFIG[route].getFullUrl();

    // Those are the routes that do not have a corresponding URL
    case Route.BOUNDARY_PROJECT_MANAGEMENT:
    case Route.BOUNDARY_PROJECT_MANAGEMENT_CREATE:
      return ROUTE_CONFIG[route].getFullUrl();

    default:
      return undefined;
  }
};

export const testRoute = ({ route, url }: { route?: Route; url: string }) => {
  if (!route) {
    return false;
  }
  return pathToRegexp(`${ROUTE_CONFIG[route].url}`).test(url);
};

export const getRouteKey = (pathname: string): Route | null => {
  const routeKey = Object.keys(ROUTE_CONFIG).find((key) => testRoute({ route: key as Route, url: pathname }));

  if (!routeKey) {
    return null;
  }
  return routeKey as Route;
};

export const getRouteConfig = (pathname: string) => {
  const routeKey = getRouteKey(pathname);
  if (!routeKey) {
    return null;
  }
  return ROUTE_CONFIG[routeKey as Route];
};

export const getProjectFlowType = (route: Route): ProjectFlowType | null => {
  if (IO_ROUTES.includes(route)) {
    return ProjectFlowType.IO;
  } else if (IP_ROUTES.includes(route)) {
    return ProjectFlowType.IP;
  }
  return null;
};

/**
 * Get the effective role for project flow
 * Adjusts role based on project ownership: PROJECT_MANAGER becomes DATA_COLLECTOR if not project owner
 * @param role - Original user role
 * @param isProjectOwner - Whether the user is project owner
 * @returns Effective role
 */
export const getEffectiveRoleForProject = (role: Role, isProjectOwner?: boolean): Role => {
  if (role === Role.PROJECT_MANAGER && !isProjectOwner) {
    return Role.DATA_COLLECTOR;
  }
  return role;
};

/**
 * Check if user has permission to access route in project flow
 * @param params - Parameters object
 * @param params.role - User account role
 * @param params.route - Route to check
 * @param params.projectAssignmentStatus - Boundary project assignment status
 * @param params.projectFlowType - Optional project flow type, will be auto-detected if not provided
 * @returns Whether user has access permission
 */
export const checkProjectFlowPermission = ({
  role,
  route,
  projectAssignmentStatus,
  projectFlowType,
}: {
  role: Role;
  route: Route;
  projectAssignmentStatus?: BoundaryProjectAssignmentStatus;
  projectFlowType?: ProjectFlowType | null;
}): boolean => {
  const { isAssignedOrg = false, isAssignedCfp = false, isProjectOwner = false } = projectAssignmentStatus || {};
  const { roles } = ROUTE_CONFIG[route];
  const effectiveRole = getEffectiveRoleForProject(role, isProjectOwner);

  // Check basic role permission
  let hasAccess = roles === undefined || roles.includes(effectiveRole);

  // When the role is DATA_COLLECTOR, perform an additional check to see if they have been assigned
  if (hasAccess && effectiveRole === Role.DATA_COLLECTOR) {
    const routeType = projectFlowType ?? getProjectFlowType(route);
    if (routeType === ProjectFlowType.IO && !isAssignedOrg) {
      hasAccess = false;
    } else if (routeType === ProjectFlowType.IP && !isAssignedCfp) {
      hasAccess = false;
    }
  }

  return hasAccess;
};
